import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

export const FAQ_ITEMS = [
  {
    q: "Is PrestiqueResume really ATS-friendly?",
    a: "Yes. Every one of our 20 foundations is tested for compatibility with applicant tracking systems (Workday, Greenhouse, Lever, iCIMS, Taleo). Single-column layouts pass at the highest tier; visual layouts with sidebars still parse cleanly on modern ATS. Look for the green ATS badge on every template card.",
  },
  {
    q: "How long does it take to build a CV?",
    a: "Most users finish in 3–5 minutes. Upload an old CV or LinkedIn PDF and our AI extracts the structure automatically — you only polish the prose. Start from scratch and the live preview keeps you focused on content, not formatting.",
  },
  {
    q: "Will the AI invent things in my CV?",
    a: "No. Our AI rewrites only what you've already written — your existing bullets, summary, and experience. It never fabricates employers, dates, metrics, or skills. Every rewrite preserves facts and only improves clarity, tone, and impact.",
  },
  {
    q: "What's the difference between PDF and Word export?",
    a: "PDF is print-ready and pixel-perfect — best for emailing or sharing publicly. Word (DOCX) is editable and best when an application portal asks for an editable file. Both formats are ATS-readable.",
  },
  {
    q: "Can I cancel my subscription any time?",
    a: "Yes. Cancel from your account page — your subscription stays active until the end of the current billing period, and your data stays exportable. No retention tricks, no hidden fees.",
  },
  {
    q: "Is my data secure?",
    a: "Yes. All data is encrypted in transit and at rest. We're GDPR-compliant — you can request a full data export or deletion at any time. We never share your CV content with third parties.",
  },
  {
    q: "Do I need to pay before I can build a CV?",
    a: "No. Building is completely free. You can preview every foundation and customise your CV without spending anything. You only pay credits when you export a polished PDF/DOCX (5 credits per export).",
  },
  {
    q: "Will recruiters know I used an AI builder?",
    a: "No. The output reads like a person wrote it — that's our entire focus. We avoid the AI cliché phrases ('passionate self-starter', 'results-driven leader') that other tools generate, so your CV stands out without sounding generic.",
  },
];

export const FaqSection = ({ items = FAQ_ITEMS, title = "Frequently asked questions" }) => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="faq-section" className="bg-bone border-y border-ink/15">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.1] mb-12">{title}</h2>
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="bg-cream border border-ink/10 rounded-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`faq-trigger-${i}`}
                aria-expanded={open === i}
                className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-white/50 transition-colors"
              >
                <span className="font-display text-lg leading-tight pr-4">{item.q}</span>
                <ChevronDown
                  className={`w-5 h-5 text-ink/50 transition-transform shrink-0 ${open === i ? "rotate-180" : ""}`}
                />
              </button>
              {open === i && (
                <div data-testid={`faq-answer-${i}`} className="px-6 pb-6 text-ink/75 text-sm leading-relaxed">
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
